<template>
    <admin-template>
        <div class="space-y-6">

            <!-- Page header -->
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-2xl font-black tracking-tight text-black">Commerciaux</h1>
                    <p class="text-sm text-gray-400 mt-0.5">Vue d'ensemble · {{ dateLabel }}</p>
                </div>
                <router-link to="/admin/commerciaux/liste" class="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold shadow-sm hover:shadow-md transition-all">
                    <i class="fas fa-users"></i> Gérer les commerciaux
                </router-link>
            </div>

            <!-- KPIs -->
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div v-for="kpi in kpis" :key="kpi.label" class="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                    <div class="flex items-start justify-between mb-3">
                        <span class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ kpi.label }}</span>
                        <div class="w-8 h-8 rounded-lg flex items-center justify-center" :class="kpi.iconBg">
                            <i :class="[kpi.icon, kpi.iconColor, 'text-sm']"></i>
                        </div>
                    </div>
                    <div class="text-2xl font-black tracking-tight" :class="kpi.valueColor || 'text-black'">{{ kpi.value }}</div>
                    <div class="text-xs text-gray-400 mt-1">{{ kpi.sub }}</div>
                </div>
            </div>

            <!-- Classement + Évolution -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

                <!-- Classement -->
                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div class="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
                        <div>
                            <h2 class="font-black text-base text-black">🏆 Classement</h2>
                            <p class="text-xs text-gray-400">Par clients apportés</p>
                        </div>
                    </div>
                    <div class="divide-y divide-gray-50">
                        <div v-if="loading" class="p-8 text-center text-gray-300 text-sm">
                            <i class="fas fa-spinner fa-spin text-xl mb-2 block"></i>Chargement...
                        </div>
                        <div v-else-if="!stats.classement || stats.classement.length === 0" class="p-8 text-center text-gray-300 text-sm">
                            <i class="fas fa-users-slash text-2xl mb-2 block"></i>Aucun commercial
                        </div>
                        <div v-for="(c, i) in stats.classement" :key="c.id" class="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors">
                            <div class="text-lg w-7 text-center flex-shrink-0">
                                <span v-if="i === 0">🥇</span>
                                <span v-else-if="i === 1">🥈</span>
                                <span v-else-if="i === 2">🥉</span>
                                <span v-else class="text-sm font-bold text-gray-300">{{ i + 1 }}</span>
                            </div>
                            <div class="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0" :style="{ background: avatarColor(c.nom) }">
                                {{ initials(c.nom) }}
                            </div>
                            <div class="flex-1 min-w-0">
                                <div class="font-bold text-sm text-black">{{ c.nom }}</div>
                                <div class="text-xs text-gray-400">{{ c.nb_clients }} client{{ c.nb_clients > 1 ? 's' : '' }} · {{ c.mrr_commission | eur }}/mois</div>
                            </div>
                            <div class="text-right flex-shrink-0">
                                <div class="text-sm font-black" :class="c.total_du > 0 ? 'text-orange-500' : 'text-green-500'">
                                    {{ c.total_du | eur }}
                                </div>
                                <div class="text-xs text-gray-400">{{ c.total_du > 0 ? 'à verser' : 'à jour' }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Évolution 12 mois -->
                <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div class="px-6 py-4 border-b border-gray-50">
                        <h2 class="font-black text-base text-black">📈 Évolution des commissions</h2>
                        <p class="text-xs text-gray-400">12 derniers mois</p>
                    </div>
                    <div class="p-6">
                        <div v-if="!stats.evolution || stats.evolution.length === 0" class="h-32 flex items-center justify-center text-gray-300 text-sm">
                            <div class="text-center"><i class="fas fa-chart-bar text-2xl mb-2 block"></i>Pas encore de données</div>
                        </div>
                        <div v-else class="flex items-end gap-1.5 h-32">
                            <div v-for="(e, i) in stats.evolution" :key="i" class="flex-1 flex flex-col items-center gap-1 h-full justify-end group cursor-default" :title="e.label + ' : ' + formatEur(e.total_commissions)">
                                <div class="text-[9px] text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">{{ formatEur(e.total_commissions) }}</div>
                                <div class="w-full rounded-t-md transition-all duration-500 min-h-[4px]"
                                    :class="isCurrentMonth(e) ? 'bg-gradient-to-t from-red-500 to-orange-400' : 'bg-gray-100 group-hover:bg-gray-200'"
                                    :style="{ height: evoBarH(e.total_commissions) + '%' }">
                                </div>
                                <div class="text-[9px] text-gray-300">{{ e.label.slice(0,3) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tableau solde par commercial -->
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
                    <div>
                        <h2 class="font-black text-base text-black">💰 Solde par commercial</h2>
                        <p class="text-xs text-gray-400">Cliquez sur un commercial pour voir son détail et payer</p>
                    </div>
                </div>

                <div v-if="loading" class="p-8 text-center text-gray-300 text-sm">
                    <i class="fas fa-spinner fa-spin text-xl mb-2 block"></i>Chargement...
                </div>
                <div v-else-if="!stats.classement || stats.classement.length === 0" class="p-8 text-center text-gray-300 text-sm">
                    <i class="fas fa-inbox text-2xl mb-2 block"></i>Aucun commercial enregistré
                </div>
                <div v-else class="overflow-x-auto">
                    <table class="w-full text-sm">
                        <thead>
                            <tr class="bg-gray-50 text-left">
                                <th class="px-6 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Commercial</th>
                                <th class="px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Taux</th>
                                <th class="px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Clients</th>
                                <th class="px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Comm. ce mois</th>
                                <th class="px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Versé total</th>
                                <th class="px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Dû total</th>
                                <th class="px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-50">
                            <tr v-for="c in stats.classement" :key="c.id" class="hover:bg-gray-50 transition-colors">
                                <td class="px-6 py-4">
                                    <div class="flex items-center gap-3">
                                        <div class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black flex-shrink-0" :style="{ background: avatarColor(c.nom) }">{{ initials(c.nom) }}</div>
                                        <span class="font-semibold text-black">{{ c.nom }}</span>
                                    </div>
                                </td>
                                <td class="px-4 py-4">
                                    <div class="flex flex-col gap-0.5">
                                        <span class="text-xs"><span class="font-bold text-orange-500">{{ c.pourcentage_m1 }}%</span> <span class="text-gray-400">M1</span></span>
                                        <span class="text-xs"><span class="font-bold text-blue-500">{{ c.pourcentage_year }}%</span> <span class="text-gray-400">récurrent</span></span>
                                    </div>
                                </td>
                                <td class="px-4 py-4 font-bold text-black">{{ c.nb_clients }}</td>
                                <td class="px-4 py-4 font-bold text-black">{{ c.mrr_commission | eur }}</td>
                                <td class="px-4 py-4 font-bold text-green-600">{{ c.total_paye | eur }}</td>
                                <td class="px-4 py-4">
                                    <span class="px-3 py-1 rounded-full text-xs font-bold"
                                        :class="c.total_du > 0 ? 'bg-orange-50 text-orange-600' : 'bg-green-50 text-green-600'">
                                        {{ c.total_du | eur }}
                                    </span>
                                </td>
                                <td class="px-4 py-4">
                                    <button @click="openDetail(c)" class="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-bold transition-colors flex items-center gap-1.5">
                                        <i class="fas fa-eye"></i> Voir & payer
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

        </div>

        <!-- Modal détail + paiement commercial -->
        <transition name="modal">
            <div v-if="detailModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
                <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="detailModal = false"></div>
                <div class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">

                    <!-- Modal header -->
                    <div class="px-6 py-5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-full flex items-center justify-center text-white font-black" :style="{ background: avatarColor(selectedCommercial.nom) }">
                                {{ initials(selectedCommercial.nom) }}
                            </div>
                            <div>
                                <h3 class="font-black text-black text-base">{{ selectedCommercial.nom }}</h3>
                                <p class="text-xs text-gray-400">{{ selectedCommercial.email }}</p>
                            </div>
                        </div>
                        <button @click="detailModal = false" class="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                            <i class="fas fa-times text-gray-500"></i>
                        </button>
                    </div>

                    <!-- Totaux -->
                    <div class="grid grid-cols-2 gap-px bg-gray-100 flex-shrink-0">
                        <div class="bg-white px-6 py-4">
                            <div class="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Total dû</div>
                            <div class="text-2xl font-black text-orange-500">{{ comDetail ? (comDetail.total_global_du | eur) : '...' }}</div>
                        </div>
                        <div class="bg-white px-6 py-4">
                            <div class="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Total versé</div>
                            <div class="text-2xl font-black text-green-500">{{ comDetail ? (comDetail.total_global_paye | eur) : '...' }}</div>
                        </div>
                    </div>

                    <!-- Liste des mois -->
                    <div class="overflow-y-auto flex-1">
                        <div v-if="detailLoading" class="p-8 text-center text-gray-300">
                            <i class="fas fa-spinner fa-spin text-2xl mb-2 block"></i> Chargement...
                        </div>
                        <table v-else class="w-full text-sm">
                            <thead class="sticky top-0 bg-gray-50 z-10">
                                <tr>
                                    <th class="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Mois</th>
                                    <th class="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Clients</th>
                                    <th class="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Montant</th>
                                    <th class="px-4 py-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Statut</th>
                                    <th class="px-4 py-3 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">Action</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-50">
                                <tr v-for="r in comDetail.recap" :key="r.periode" :class="r.tout_paye ? 'opacity-50' : ''">
                                    <td class="px-6 py-3 font-semibold text-black">{{ r.mois | moisLabel }} {{ r.annee }}</td>
                                    <td class="px-4 py-3 text-gray-500">{{ r.nb_clients }}</td>
                                    <td class="px-4 py-3 font-bold text-black">{{ r.total_mois | eur }}</td>
                                    <td class="px-4 py-3">
                                        <span class="px-2.5 py-1 rounded-full text-xs font-bold"
                                            :class="r.tout_paye ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-600'">
                                            {{ r.tout_paye ? '✓ Versé' : 'En attente' }}
                                        </span>
                                    </td>
                                    <td class="px-4 py-3 text-right">
                                        <button v-if="!r.tout_paye"
                                            @click="payerMois(r)"
                                            :disabled="payingRow === r.periode"
                                            class="px-3 py-1.5 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold hover:shadow-md transition-all disabled:opacity-50">
                                            {{ payingRow === r.periode ? '...' : 'Marquer versé' }}
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </transition>

    </admin-template>
</template>

<script>
export default {
    name: 'AdminCommerciauxDashboard',

    filters: {
        eur(v) {
            if (v === null || v === undefined) return '—';
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
        },
        moisLabel(m) {
            return ['','Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'][m] || m;
        },
    },

    data() {
        return {
            loading: true,
            stats: { classement: [], evolution: [], mrr_commissions: 0, total_du: 0, total_paye: 0, nb_commerciaux: 0 },
            detailModal: false,
            detailLoading: false,
            selectedCommercial: null,
            comDetail: null,
            payingRow: null,
        };
    },

    computed: {
        dateLabel() {
            return new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        },
        kpis() {
            return [
                {
                    label: 'Commissions ce mois',
                    value: this.formatEur(this.stats.mrr_commissions),
                    sub: 'Total dû aux commerciaux',
                    icon: 'fas fa-coins',
                    iconBg: 'bg-orange-50',
                    iconColor: 'text-orange-500',
                    valueColor: 'text-black',
                },
                {
                    label: 'Total dû (impayé)',
                    value: this.formatEur(this.stats.total_du),
                    sub: 'À verser aux commerciaux',
                    icon: 'fas fa-hourglass-half',
                    iconBg: 'bg-red-50',
                    iconColor: 'text-red-500',
                    valueColor: this.stats.total_du > 0 ? 'text-orange-500' : 'text-black',
                },
                {
                    label: 'Total versé',
                    value: this.formatEur(this.stats.total_paye),
                    sub: 'Commissions déjà payées',
                    icon: 'fas fa-check-double',
                    iconBg: 'bg-green-50',
                    iconColor: 'text-green-500',
                    valueColor: 'text-green-600',
                },
                {
                    label: 'Commerciaux actifs',
                    value: this.stats.nb_commerciaux,
                    sub: 'Sur le terrain',
                    icon: 'fas fa-user-tie',
                    iconBg: 'bg-blue-50',
                    iconColor: 'text-blue-500',
                    valueColor: 'text-black',
                },
            ];
        },
    },

    mounted() {
        this.fetchStats();
    },

    methods: {
        async fetchStats() {
            this.loading = true;
            const r = await this.$request.get('/admin/commerciaux/stats-globales');
            this.stats = r || { classement: [], evolution: [], mrr_commissions: 0, total_du: 0, total_paye: 0, nb_commerciaux: 0 };
            this.loading = false;
        },

        async openDetail(c) {
            this.selectedCommercial = c;
            this.comDetail = null;
            this.detailModal = true;
            this.detailLoading = true;
            const r = await this.$request.get(`/admin/commerciaux/${c.id}/recap-paiements`);
            this.comDetail = r;
            this.detailLoading = false;
        },

        async payerMois(r) {
            this.payingRow = r.periode;
            try {
                await this.$request.post(`/admin/commerciaux/${this.selectedCommercial.id}/payer`, {
                    mois: r.mois,
                    annee: r.annee,
                });
                // Rafraîchir le détail
                const res = await this.$request.get(`/admin/commerciaux/${this.selectedCommercial.id}/recap-paiements`);
                this.comDetail = res;
                await this.fetchStats();
            } catch (e) {
                console.error(e);
            }
            this.payingRow = null;
        },

        evoBarH(v) {
            if (!this.stats.evolution?.length) return 0;
            const max = Math.max(...this.stats.evolution.map(e => e.total_commissions || 0));
            return max ? Math.max(6, Math.round(v / max * 100)) : 6;
        },
        isCurrentMonth(e) {
            return e.mois === new Date().getMonth() + 1 && e.annee === new Date().getFullYear();
        },
        initials(n) { return (n || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2); },
        avatarColor(n) {
            const colors = ['#F97316', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#06B6D4', '#F59E0B'];
            let h = 0; for (let i = 0; i < (n || '').length; i++) h = n.charCodeAt(i) + ((h << 5) - h);
            return colors[Math.abs(h) % colors.length];
        },
        formatEur(v) {
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
        },
    },
};
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter, .modal-leave-to { opacity: 0; }
</style>